import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

// UNIFIED API - Django handles everything now
// Django User Service - handles auth, wallet management, user data, performance, bot control
const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000/api';

// Main API client - Django handles all endpoints
const api = axios.create({
  baseURL: API_BASE,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Legacy aliases for backward compatibility
const authApi = api;
const botApi = api;

// Token refresh state management
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

// Process queued requests after token refresh
const processQueue = (error: AxiosError | null, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Event for triggering logout from outside React context
const AUTH_LOGOUT_EVENT = 'auth:logout';

export const triggerLogout = () => {
  window.dispatchEvent(new CustomEvent(AUTH_LOGOUT_EVENT));
};

export const onLogoutEvent = (callback: () => void) => {
  window.addEventListener(AUTH_LOGOUT_EVENT, callback);
  return () => window.removeEventListener(AUTH_LOGOUT_EVENT, callback);
};

// Response interceptor for handling 401 errors and token refresh
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // If error is not 401 or request already retried, reject
    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    // Skip token refresh for auth endpoints to avoid infinite loops
    if (originalRequest.url?.includes('/auth/token')) {
      return Promise.reject(error);
    }

    const refreshToken = localStorage.getItem('refreshToken');

    // No refresh token available, trigger logout
    if (!refreshToken) {
      triggerLogout();
      return Promise.reject(error);
    }

    // If already refreshing, queue this request
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((token) => {
          if (originalRequest.headers) {
            originalRequest.headers['Authorization'] = `Bearer ${token}`;
          }
          return api(originalRequest);
        })
        .catch((err) => {
          return Promise.reject(err);
        });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      // Attempt to refresh the token
      const response = await axios.post(`${API_BASE}/auth/token/refresh/`, {
        refresh: refreshToken,
      });

      const newAccessToken = response.data.access;
      const newRefreshToken = response.data.refresh; // SimpleJWT rotates refresh tokens

      // Update stored tokens
      localStorage.setItem('token', newAccessToken);
      if (newRefreshToken) {
        localStorage.setItem('refreshToken', newRefreshToken);
      }

      // Update axios default header
      api.defaults.headers.common['Authorization'] = `Bearer ${newAccessToken}`;

      // Process queued requests with new token
      processQueue(null, newAccessToken);

      // Retry original request with new token
      if (originalRequest.headers) {
        originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
      }
      return api(originalRequest);
    } catch (refreshError) {
      // Refresh failed - clear tokens and trigger logout
      processQueue(refreshError as AxiosError, null);
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      triggerLogout();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

export interface HealthStatus {
  status: 'healthy' | 'degraded' | 'unhealthy';
  timestamp: string;
  uptime: number;
  version: string;
  components: {
    liquidity_manager: ComponentStatus;
    position_monitor: ComponentStatus;
    price_feeds: ComponentStatus;
  };
}

export interface ComponentStatus {
  status: 'up' | 'down';
  lastCheck: string;
  responseTime: number;
}

// TWR Chart Data Type
export interface TWRChartData {
  timestamps: string[];
  twr_daily: number[];
  twr_weekly: number[];
  twr_monthly: number[];
  twr_total: number[];
  period: string;
  source?: string;
}

export interface DeltaData {
  delta: number;
  lpNotional: number;
  timestamp: string;
  confidence: number;
  tokens: {
    tokenA: TokenInfo;
    tokenB: TokenInfo;
  };
  positions: PositionDelta[];
  metadata: {
    totalPositions: number;
    activeMarkets: string[];
    lastUpdate: string;
  };
}

export interface TokenInfo {
  symbol: string;
  balance: number;
  price: number;
  valueUsd: number;
  delta: number;
}

export interface PositionDelta {
  poolId: string;
  tokenABalance: number;
  tokenBBalance: number;
  deltaUsd: number;
}

export interface AuthResponse {
  token: string;
  refreshToken: string;
  user: {
    id: string;
    email: string;
    subscription_status: string;
    subscription_tier: string;
    is_admin?: boolean;
    wallet_address?: string;
    has_completed_onboarding?: boolean;
  };
}

export interface OnboardingStatus {
  has_completed_onboarding: boolean;
  risk_acknowledged_at: string | null;
  onboarding_completed_at: string | null;
}

export interface CompleteOnboardingResponse {
  success: boolean;
  message: string;
  has_completed_onboarding: boolean;
  risk_acknowledged_at: string;
  onboarding_completed_at: string;
}

// Django SimpleJWT raw response format
interface DjangoJWTResponse {
  access: string;
  refresh?: string;
  user?: {
    id?: string;
    email?: string;
    subscription_status?: string;
    subscription_tier?: string;
    is_admin?: boolean;
    wallet_address?: string;
    has_completed_onboarding?: boolean;
  };
}

export interface PerformanceData {
  timestamp: string;
  total_value_usd: number;
  wallet_balance_sol: number;
  wallet_balance_usdc: number;
  drift_balance_usd: number;
  orca_positions_value_usd: number;
  total_fees_earned: number;
  unrealized_pnl: number;
  realized_pnl: number;
}

export interface PerformanceHistory {
  snapshots: PerformanceData[];
  current_value: number;
  initial_value: number;
  value_7d_ago: number;
  value_30d_ago: number;
  total_return: number;
  total_return_percent: number;
  change_24h: number;
  change_24h_percent: number;
  change_7d: number;
  change_7d_percent: number;
  change_30d: number;
  change_30d_percent: number;
}

export interface ChartData {
  timestamps: string[];
  values: number[];
  period: string;
  granularity?: 'hourly' | 'daily';
}

export interface DailyPerformanceItem {
  date: string;
  timestamp: string;
  total_value_usd: number;
  daily_change_usd: number;
  daily_change_percent: number;

  // Trading changes (excluding cash flows)
  trading_change_usd: number;
  trading_change_percent: number;

  // Cash flow data
  has_cash_flow: boolean;
  cash_flow_amount: number;
  cash_flow_type?: string | null;
  cash_flow_confidence?: number | null;
  cash_flow_verified?: boolean;

  // Asset balances
  wallet_balance_sol: number;
  wallet_balance_usdc: number;
  sol_price: number;

  // Protocol breakdowns
  drift: {
    total_value: number;
    free_collateral: number;
    unrealized_pnl: number;
    daily_change: number;
  };
  orca: {
    total_value: number;
    positions_value: number;
    uncollected_fees: number;
    daily_change: number;
  };

  // TWR fields (optional for backward compatibility)
  twr_daily?: number;
  twr_cumulative?: number;
}

export interface DailyPerformanceResponse {
  data: DailyPerformanceItem[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

// TWR (Time-Weighted Returns) Types
export interface TWRMetrics {
  daily: number;
  weekly: number;
  monthly: number;
  total: number;
  last_calculated: string;
}

export interface CashFlow {
  id: string;
  wallet_address: string;
  wallet_name: string;
  timestamp: string;
  type: 'deposit' | 'withdrawal';
  amount_usd: number;
  detection_method: 'historical' | 'initial' | 'threshold' | 'manual' | 'transaction';
  confidence: number;
  verified: boolean;
  notes?: string;
}

export interface PeriodMetrics {
  today: {
    absolute_change: number;
    absolute_percent: number;
    twr_percent: number;
  };
  mtd: {
    absolute_change: number;
    absolute_percent: number;
    twr_percent: number;
  };
  ytd: {
    absolute_change: number;
    absolute_percent: number;
    twr_percent: number;
  };
  last_24h: {
    absolute_change: number;
    absolute_percent: number;
    twr_percent: number;
  };
}

export interface RiskMetrics {
  volatility_daily: number;
  volatility_annualized: number;
  sharpe_ratio: number;
  max_drawdown: number;
  max_drawdown_percent: number;
  win_rate: number;
  profit_factor: number;
  avg_win: number;
  avg_loss: number;
}

export interface TWRPerformance {
  portfolio_value: number;

  // Absolute returns (includes deposits/withdrawals)
  absolute_return: number;
  absolute_percent: number;

  // TWR metrics (true trading performance)
  twr: TWRMetrics;

  // Cash flow summary
  net_invested: number;
  total_deposits: number;
  total_withdrawals: number;

  // True P&L
  trading_pnl: number;  // portfolio_value - net_invested
  roi_percent: number;  // trading_pnl / net_invested * 100

  // Time periods
  periods: {
    daily: {
      absolute_return: number;
      absolute_percent: number;
      twr_return: number;
      twr_percent: number;
    };
    weekly: {
      absolute_return: number;
      absolute_percent: number;
      twr_return: number;
      twr_percent: number;
    };
    monthly: {
      absolute_return: number;
      absolute_percent: number;
      twr_return: number;
      twr_percent: number;
    };
    total: {
      absolute_return: number;
      absolute_percent: number;
      twr_return: number;
      twr_percent: number;
    };
  };
}

export interface PerformanceComparison {
  current_value: number;
  initial_value: number;
  net_invested: number;

  absolute: {
    total_return: number;
    total_percent: number;
    daily_change: number;
    daily_percent: number;
    weekly_change: number;
    weekly_percent: number;
    monthly_change: number;
    monthly_percent: number;
  };

  twr: {
    total_return: number;
    total_percent: number;
    daily_return: number;
    daily_percent: number;
    weekly_return: number;
    weekly_percent: number;
    monthly_return: number;
    monthly_percent: number;
  };

  cash_flows: {
    total_deposits: number;
    total_withdrawals: number;
    net_flow: number;
    last_flow_date?: string;
  };
}

// Withdrawal Types
export interface WithdrawalRequest {
  id: number;
  wallet: number;
  wallet_address: string;
  amount: number;
  amount_usd: number;
  token: 'SOL' | 'USDC';
  destination_address: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
  created_at: string;
  updated_at: string;
  processed_at?: string;
  transaction_signature?: string;
  actual_amount?: number;
  fee_amount?: number;
  error_message?: string;
  retry_count: number;
}

export interface WithdrawalLimits {
  available_sol: number;
  available_usdc: number;
  min_withdrawal_sol: number;
  min_withdrawal_usdc: number;
  daily_limit_usd: number;
  daily_used_usd: number;
  daily_remaining_usd: number;
  sol_price?: number;
}

export interface WithdrawalCreateRequest {
  amount: number;
  token: 'SOL' | 'USDC';
  destination_address: string;
  password: string;
}

// Swap Types
export interface SwapRequest {
  id: number;
  wallet: number;
  wallet_address: string;
  input_token: 'SOL' | 'USDC';
  output_token: 'SOL' | 'USDC';
  input_amount: number;
  input_amount_usd: number;
  quoted_output_amount?: number;
  slippage_bps: number;
  price_impact_percent?: number;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
  created_at: string;
  updated_at: string;
  processed_at?: string;
  transaction_signature?: string;
  actual_output_amount?: number;
  fee_amount?: number;
  error_message?: string;
  retry_count: number;
}

export interface SwapBalances {
  available_sol: number;
  available_usdc: number;
  max_swap_sol: number;
  max_swap_usdc: number;
  gas_reserve_sol: number;
  sol_price: number;
}

export interface SwapQuote {
  input_token: 'SOL' | 'USDC';
  output_token: 'SOL' | 'USDC';
  input_amount: number;
  output_amount: number;
  input_amount_usd: number;
  output_amount_usd: number;
  price_impact_percent: number;
  slippage_bps: number;
  exchange_rate: number;
  sol_price: number;
}

export interface SwapCreateRequest {
  input_token: 'SOL' | 'USDC';
  output_token: 'SOL' | 'USDC';
  input_amount: number;
  slippage_bps?: number;
  password: string;
}

// Subscription & Payment Types
export interface SubscriptionStatus {
  tier: string;
  tier_display: string;
  status: string;
  status_display: string;
  is_active: boolean;
  can_start_bot: boolean;
  started_at: string;
  expires_at: string | null;
  max_capital_usd: number;
  max_positions: number;
  limits?: {
    max_capital_usd: number;
    max_positions: number;
    features: string[];
  };
  free_tier_eligible?: boolean;
  wallet_balance_usd?: number | null;
  balance_check_error?: string | null;
}

export interface Plan {
  id: number;
  name: string;
  price_usdc: number;
  is_active: boolean;
  is_lifetime: boolean;
}

export interface PaymentRequest {
  id: number;
  plan: Plan;
  amount_usdc: number;
  reference: string;
  signature: string | null;
  status: 'pending' | 'confirmed' | 'expired' | 'failed';
  receiving_wallet: string;
  created_at: string;
  confirmed_at: string | null;
  expires_at: string;
  payment_url: string | null;
  qr_data: string | null;
}

export interface PaymentStatusResponse {
  id: number;
  status: 'pending' | 'confirmed' | 'expired' | 'failed';
  signature: string | null;
  confirmed_at: string | null;
  expires_at: string;
}

export interface PaymentVerifyResponse {
  status: 'confirmed' | 'pending';
  signature?: string;
  message: string;
}

export const apiService = {
  setAuthToken: (token: string | null) => {
    if (token) {
      // Set token for the unified API client
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete api.defaults.headers.common['Authorization'];
    }
  },

  // Auth endpoints (Django User Service)
  login: async (email: string, password: string): Promise<AuthResponse> => {
    const response = await authApi.post<DjangoJWTResponse>('/auth/token/', { email, password });
    // Django SimpleJWT returns 'access' and 'refresh' tokens
    return {
      token: response.data.access,
      refreshToken: response.data.refresh || '',
      user: {
        id: response.data.user?.id || '',
        email: email,
        subscription_status: response.data.user?.subscription_status || 'active',
        subscription_tier: response.data.user?.subscription_tier || 'free',
        is_admin: response.data.user?.is_admin,
        wallet_address: response.data.user?.wallet_address,
        has_completed_onboarding: response.data.user?.has_completed_onboarding ?? false,
      }
    };
  },

  register: async (email: string, password: string, passwordConfirm: string): Promise<AuthResponse> => {
    const response = await authApi.post('/auth/register/', {
      email,
      password,
      password_confirm: passwordConfirm
    });
    // Django returns: { token, refresh_token, user, wallet_address }
    // New users have not completed onboarding
    return {
      token: response.data.token,
      refreshToken: response.data.refresh_token || '',
      user: {
        id: response.data.user.id,
        email: response.data.user.email,
        subscription_status: 'active',
        subscription_tier: 'free',
        is_admin: false,
        wallet_address: response.data.wallet_address || response.data.user.wallet_address,
        has_completed_onboarding: false,
      },
    };
  },

  // Performance endpoints
  getCurrentPerformance: async (): Promise<PerformanceData> => {
    const response = await api.get<PerformanceData>('/performance/current');
    return response.data;
  },

  getPerformanceHistory: async (period?: string): Promise<PerformanceHistory> => {
    const response = await api.get<PerformanceHistory>('/performance/history', {
      params: { period }
    });
    return response.data;
  },

  getChartData: async (period?: string, resolution?: string): Promise<ChartData> => {
    const response = await api.get<ChartData>('/performance/chart', {
      params: { period, resolution }
    });
    return response.data;
  },

  getDailyPerformance: async (limit: number = 10, offset: number = 0): Promise<DailyPerformanceResponse> => {
    const response = await api.get<DailyPerformanceResponse>('/performance/daily', {
      params: { limit, offset }
    });
    return response.data;
  },

  // Legacy endpoints (for backward compatibility)
  getHealth: async (): Promise<HealthStatus> => {
    const response = await api.get<HealthStatus>('/health');
    return response.data;
  },

  getDelta: async (): Promise<DeltaData> => {
    const response = await api.get<DeltaData>('/delta');
    return response.data;
  },

  // Admin endpoints
  getAdminUsers: async (): Promise<any[]> => {
    const response = await api.get('/admin/users');
    return response.data;
  },

  getAdminUserPerformance: async (userId: string, period?: string): Promise<PerformanceHistory> => {
    const response = await api.get(`/admin/performance/history/${userId}`, {
      params: { period }
    });
    return response.data;
  },

  getConsolidatedPerformance: async (): Promise<any> => {
    const response = await api.get('/admin/performance/consolidated');
    return response.data;
  },

  getConsolidatedChart: async (period?: string): Promise<ChartData> => {
    const response = await api.get('/admin/performance/consolidated/chart', {
      params: { period }
    });
    return response.data;
  },

  getAdminUserChart: async (userId: string, period?: string): Promise<ChartData> => {
    const response = await api.get(`/admin/performance/chart/${userId}`, {
      params: { period }
    });
    return response.data;
  },

  getConsolidatedDailyPerformance: async (limit: number = 10, offset: number = 0): Promise<DailyPerformanceResponse> => {
    const response = await api.get<DailyPerformanceResponse>('/admin/performance/consolidated/daily', {
      params: { limit, offset }
    });
    return response.data;
  },

  getAdminUserDailyPerformance: async (userId: string, limit: number = 10, offset: number = 0): Promise<DailyPerformanceResponse> => {
    const response = await api.get<DailyPerformanceResponse>(`/admin/performance/daily/${userId}`, {
      params: { limit, offset }
    });
    return response.data;
  },

  // TWR chart endpoints
  getTWRChart: async (period?: string): Promise<TWRChartData> => {
    const response = await api.get('/performance/twr/chart', {
      params: { period }
    });
    return response.data;
  },

  getAdminUserTWRChart: async (userId: string, period?: string): Promise<TWRChartData> => {
    const response = await api.get(`/admin/performance/twr/chart/${userId}`, {
      params: { period }
    });
    return response.data;
  },

  getConsolidatedTWRChart: async (period?: string): Promise<TWRChartData> => {
    const response = await api.get('/admin/performance/consolidated/twr/chart', {
      params: { period }
    });
    return response.data;
  },

  // TWR endpoints
  getTWRPerformance: async (): Promise<TWRPerformance> => {
    const response = await api.get('/performance/twr');
    const data = response.data;

    // Transform the backend response to match the expected TWRPerformance interface
    // Calculate aggregated TWR metrics from wallet data
    let aggregatedTWR = {
      daily: 0,
      weekly: 0,
      monthly: 0,
      total: 0,
      last_calculated: new Date().toISOString()
    };

    // If we have wallet data, calculate weighted average TWR based on wallet values
    if (data.wallets && data.wallets.length > 0) {
      let totalValue = 0;
      let weightedDaily = 0;
      let weightedWeekly = 0;
      let weightedMonthly = 0;
      let weightedTotal = 0;

      data.wallets.forEach((wallet: any) => {
        const walletValue = wallet.current_value || 0;
        totalValue += walletValue;

        if (wallet.twr) {
          weightedDaily += (wallet.twr.daily || 0) * walletValue;
          weightedWeekly += (wallet.twr.weekly || 0) * walletValue;
          weightedMonthly += (wallet.twr.monthly || 0) * walletValue;
          weightedTotal += (wallet.twr.total || 0) * walletValue;
        }
      });

      if (totalValue > 0) {
        aggregatedTWR.daily = weightedDaily / totalValue;
        aggregatedTWR.weekly = weightedWeekly / totalValue;
        aggregatedTWR.monthly = weightedMonthly / totalValue;
        aggregatedTWR.total = weightedTotal / totalValue;
      }
    }

    // If portfolio has twr_percent but no nested twr object, use it for total
    if (data.portfolio && typeof data.portfolio.twr_percent === 'number' && !aggregatedTWR.total) {
      aggregatedTWR.total = data.portfolio.twr_percent;
    }

    const absoluteReturn = (data.portfolio?.current_value || 0) - (data.portfolio?.net_invested || 0);
    const absolutePercent = data.portfolio?.net_invested > 0
      ? (absoluteReturn / data.portfolio.net_invested * 100)
      : 0;

    return {
      portfolio_value: data.portfolio?.current_value || 0,
      absolute_return: absoluteReturn,
      absolute_percent: absolutePercent,
      twr: aggregatedTWR,
      net_invested: data.portfolio?.net_invested || 0,
      total_deposits: data.portfolio?.total_deposits || 0,
      total_withdrawals: data.portfolio?.total_withdrawals || 0,
      trading_pnl: absoluteReturn,
      roi_percent: absolutePercent,
      periods: {
        daily: {
          absolute_return: 0,  // Would need daily snapshot data to calculate
          absolute_percent: 0,
          twr_return: 0,
          twr_percent: aggregatedTWR.daily
        },
        weekly: {
          absolute_return: 0,  // Would need weekly snapshot data to calculate
          absolute_percent: 0,
          twr_return: 0,
          twr_percent: aggregatedTWR.weekly
        },
        monthly: {
          absolute_return: 0,  // Would need monthly snapshot data to calculate
          absolute_percent: 0,
          twr_return: 0,
          twr_percent: aggregatedTWR.monthly
        },
        total: {
          absolute_return: absoluteReturn,
          absolute_percent: absolutePercent,
          twr_return: 0,
          twr_percent: aggregatedTWR.total
        }
      }
    };
  },

  getCashFlows: async (limit: number = 50, offset: number = 0, verified?: boolean): Promise<{ data: CashFlow[], pagination: { total: number, limit: number, offset: number, hasMore: boolean } }> => {
    const response = await api.get('/cash-flows', {
      params: { limit, offset, verified }
    });

    // Map the API response to the expected format
    const apiData = response.data;
    return {
      data: apiData.cash_flows || [],
      pagination: {
        total: apiData.total || 0,
        limit: apiData.limit || limit,
        offset: apiData.offset || offset,
        hasMore: (apiData.offset || 0) + (apiData.cash_flows?.length || 0) < (apiData.total || 0)
      }
    };
  },

  verifyCashFlow: async (cashFlowId: string, verified: boolean = true): Promise<CashFlow> => {
    const response = await api.put<CashFlow>(`/cash-flows/${cashFlowId}/verify`, { verified });
    return response.data;
  },

  addManualCashFlow: async (cashFlow: {
    timestamp: string;
    flow_type: 'deposit' | 'withdrawal';
    amount_usd: number;
    notes?: string;
  }): Promise<CashFlow> => {
    const response = await api.post<CashFlow>('/cash-flows', cashFlow);
    return response.data;
  },

  getPerformanceComparison: async (period?: string): Promise<PerformanceComparison> => {
    const response = await api.get<PerformanceComparison>('/performance/comparison', {
      params: { period }
    });
    return response.data;
  },

  // Admin TWR endpoints
  getAdminUserTWR: async (userId: string): Promise<TWRPerformance> => {
    const response = await api.get(`/admin/performance/twr/${userId}`);
    const data = response.data;

    // Transform the backend response to match the expected TWRPerformance interface
    let aggregatedTWR = {
      daily: 0,
      weekly: 0,
      monthly: 0,
      total: 0,
      last_calculated: new Date().toISOString()
    };

    // If we have wallet data, calculate weighted average TWR based on wallet values
    if (data.wallets && data.wallets.length > 0) {
      let totalValue = 0;
      let weightedDaily = 0;
      let weightedWeekly = 0;
      let weightedMonthly = 0;
      let weightedTotal = 0;

      data.wallets.forEach((wallet: any) => {
        const walletValue = wallet.current_value || 0;
        totalValue += walletValue;

        if (wallet.twr) {
          weightedDaily += (wallet.twr.daily || 0) * walletValue;
          weightedWeekly += (wallet.twr.weekly || 0) * walletValue;
          weightedMonthly += (wallet.twr.monthly || 0) * walletValue;
          weightedTotal += (wallet.twr.total || 0) * walletValue;
        }
      });

      if (totalValue > 0) {
        aggregatedTWR.daily = weightedDaily / totalValue;
        aggregatedTWR.weekly = weightedWeekly / totalValue;
        aggregatedTWR.monthly = weightedMonthly / totalValue;
        aggregatedTWR.total = weightedTotal / totalValue;
      }
    }

    // If portfolio has twr_percent but no nested twr object, use it for total
    if (data.portfolio && typeof data.portfolio.twr_percent === 'number' && !aggregatedTWR.total) {
      aggregatedTWR.total = data.portfolio.twr_percent;
    }

    const absoluteReturn = (data.portfolio?.current_value || 0) - (data.portfolio?.net_invested || 0);
    const absolutePercent = data.portfolio?.net_invested > 0
      ? (absoluteReturn / data.portfolio.net_invested * 100)
      : 0;

    return {
      portfolio_value: data.portfolio?.current_value || 0,
      absolute_return: absoluteReturn,
      absolute_percent: absolutePercent,
      twr: aggregatedTWR,
      net_invested: data.portfolio?.net_invested || 0,
      total_deposits: data.portfolio?.total_deposits || 0,
      total_withdrawals: data.portfolio?.total_withdrawals || 0,
      trading_pnl: absoluteReturn,
      roi_percent: absolutePercent,
      periods: {
        daily: {
          absolute_return: 0,  // Would need daily snapshot data to calculate
          absolute_percent: 0,
          twr_return: 0,
          twr_percent: aggregatedTWR.daily
        },
        weekly: {
          absolute_return: 0,  // Would need weekly snapshot data to calculate
          absolute_percent: 0,
          twr_return: 0,
          twr_percent: aggregatedTWR.weekly
        },
        monthly: {
          absolute_return: 0,  // Would need monthly snapshot data to calculate
          absolute_percent: 0,
          twr_return: 0,
          twr_percent: aggregatedTWR.monthly
        },
        total: {
          absolute_return: absoluteReturn,
          absolute_percent: absolutePercent,
          twr_return: 0,
          twr_percent: aggregatedTWR.total
        }
      }
    };
  },

  // Helper function to calculate period metrics from daily performance data
  calculatePeriodMetrics: async (): Promise<PeriodMetrics> => {
    try {
      // Fetch enough daily data to cover YTD
      const dailyData = await apiService.getDailyPerformance(365, 0);

      if (!dailyData.data || dailyData.data.length === 0) {
        throw new Error('No daily performance data available');
      }

      const today = new Date();
      const todayStr = today.toISOString().split('T')[0];

      // Most recent data is first in the array
      const latestData = dailyData.data[0];
      const currentValue = latestData.total_value_usd;

      // Calculate today's metrics (first row is most recent)
      const todayChange = latestData.daily_change_usd;
      const todayPercent = latestData.daily_change_percent;
      const todayTWR = latestData.trading_change_percent || 0;

      // Calculate 24h metrics (same as today)
      const change24h = todayChange;
      const percent24h = todayPercent;
      const twr24h = todayTWR;

      // Calculate MTD - find first day of current month
      let mtdChange = 0;
      let mtdPercent = 0;
      const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

      // Find the data point closest to start of month
      let mtdStartValue = currentValue; // Default to current if no data
      for (let i = 0; i < dailyData.data.length; i++) {
        const dataDate = new Date(dailyData.data[i].date);
        if (dataDate < firstDayOfMonth) {
          // This is the last day of previous month, so next value is start of this month
          if (i > 0) {
            // Get the value at start of month (end of previous day)
            mtdStartValue = dailyData.data[i].total_value_usd;
          }
          break;
        }
      }

      // If we found data from before this month
      if (mtdStartValue !== currentValue) {
        mtdChange = currentValue - mtdStartValue;
        mtdPercent = mtdStartValue > 0 ? (mtdChange / mtdStartValue) * 100 : 0;
      } else {
        // If no data from before this month, sum up all daily changes in current month
        mtdChange = 0;
        for (const data of dailyData.data) {
          const dataDate = new Date(data.date);
          if (dataDate >= firstDayOfMonth) {
            mtdChange += data.daily_change_usd;
          } else {
            break;
          }
        }
        mtdPercent = currentValue > 0 ? (mtdChange / (currentValue - mtdChange)) * 100 : 0;
      }

      // Calculate YTD - find first day of current year
      let ytdChange = 0;
      let ytdPercent = 0;
      const firstDayOfYear = new Date(today.getFullYear(), 0, 1);

      // Find the data point closest to start of year
      let ytdStartValue = currentValue; // Default to current if no data
      for (let i = 0; i < dailyData.data.length; i++) {
        const dataDate = new Date(dailyData.data[i].date);
        if (dataDate < firstDayOfYear) {
          // This is the last day of previous year
          if (i > 0) {
            ytdStartValue = dailyData.data[i].total_value_usd;
          }
          break;
        }
      }

      // If we found data from before this year
      if (ytdStartValue !== currentValue) {
        ytdChange = currentValue - ytdStartValue;
        ytdPercent = ytdStartValue > 0 ? (ytdChange / ytdStartValue) * 100 : 0;
      } else {
        // If no data from before this year, sum up all daily changes in current year
        ytdChange = 0;
        for (const data of dailyData.data) {
          const dataDate = new Date(data.date);
          if (dataDate >= firstDayOfYear) {
            ytdChange += data.daily_change_usd;
          } else {
            break;
          }
        }
        ytdPercent = currentValue > 0 ? (ytdChange / (currentValue - ytdChange)) * 100 : 0;
      }

      return {
        today: {
          absolute_change: todayChange,
          absolute_percent: todayPercent,
          twr_percent: todayTWR
        },
        mtd: {
          absolute_change: mtdChange,
          absolute_percent: mtdPercent,
          twr_percent: 0  // Would need to calculate compound TWR for month
        },
        ytd: {
          absolute_change: ytdChange,
          absolute_percent: ytdPercent,
          twr_percent: 0  // Would need to calculate compound TWR for year
        },
        last_24h: {
          absolute_change: change24h,
          absolute_percent: percent24h,
          twr_percent: twr24h
        }
      };
    } catch (error) {
      console.error('Error calculating period metrics:', error);
      return {
        today: { absolute_change: 0, absolute_percent: 0, twr_percent: 0 },
        mtd: { absolute_change: 0, absolute_percent: 0, twr_percent: 0 },
        ytd: { absolute_change: 0, absolute_percent: 0, twr_percent: 0 },
        last_24h: { absolute_change: 0, absolute_percent: 0, twr_percent: 0 }
      };
    }
  },

  // Helper function to calculate risk metrics from daily performance data
  calculateRiskMetrics: async (): Promise<RiskMetrics> => {
    try {
      const dailyData = await apiService.getDailyPerformance(365, 0);

      // Return default metrics if no data available
      if (!dailyData.data || dailyData.data.length === 0) {
        return {
          volatility_daily: 0,
          volatility_annualized: 0,
          sharpe_ratio: 0,
          max_drawdown: 0,
          max_drawdown_percent: 0,
          win_rate: 0,
          profit_factor: 0,
          avg_win: 0,
          avg_loss: 0
        };
      }

      const returns: number[] = [];
      let maxValue = 0;
      let maxDrawdown = 0;
      let winCount = 0;
      let totalWin = 0;
      let totalLoss = 0;
      let lossCount = 0;

      // Calculate returns and track metrics
      for (let i = 0; i < dailyData.data.length - 1; i++) {
        const dailyReturn = dailyData.data[i].trading_change_percent || 0;
        returns.push(dailyReturn);

        // Track max value and drawdown
        const currentValue = dailyData.data[i].total_value_usd;
        if (currentValue > maxValue) {
          maxValue = currentValue;
        }
        const drawdown = maxValue - currentValue;
        if (drawdown > maxDrawdown) {
          maxDrawdown = drawdown;
        }

        // Track wins/losses
        if (dailyReturn > 0) {
          winCount++;
          totalWin += dailyReturn;
        } else if (dailyReturn < 0) {
          lossCount++;
          totalLoss += Math.abs(dailyReturn);
        }
      }

      // Calculate volatility (standard deviation of returns)
      const avgReturn = returns.reduce((a, b) => a + b, 0) / returns.length;
      const variance = returns.reduce((sum, r) => sum + Math.pow(r - avgReturn, 2), 0) / returns.length;
      const volatilityDaily = Math.sqrt(variance);
      const volatilityAnnualized = volatilityDaily * Math.sqrt(252); // 252 trading days

      // Calculate Sharpe ratio (assume risk-free rate of 5% annually)
      const riskFreeDaily = 0.05 / 252;
      const excessReturn = avgReturn - riskFreeDaily;
      const sharpeRatio = volatilityDaily > 0 ? (excessReturn / volatilityDaily) * Math.sqrt(252) : 0;

      // Calculate other metrics
      const winRate = (winCount / (winCount + lossCount)) * 100;
      const avgWin = winCount > 0 ? totalWin / winCount : 0;
      const avgLoss = lossCount > 0 ? totalLoss / lossCount : 0;
      const profitFactor = totalLoss > 0 ? totalWin / totalLoss : totalWin > 0 ? Infinity : 0;

      return {
        volatility_daily: volatilityDaily,
        volatility_annualized: volatilityAnnualized,
        sharpe_ratio: sharpeRatio,
        max_drawdown: maxDrawdown,
        max_drawdown_percent: maxValue > 0 ? (maxDrawdown / maxValue) * 100 : 0,
        win_rate: winRate,
        profit_factor: profitFactor,
        avg_win: avgWin,
        avg_loss: avgLoss
      };
    } catch (error) {
      console.error('Error calculating risk metrics:', error);
      return {
        volatility_daily: 0,
        volatility_annualized: 0,
        sharpe_ratio: 0,
        max_drawdown: 0,
        max_drawdown_percent: 0,
        win_rate: 0,
        profit_factor: 0,
        avg_win: 0,
        avg_loss: 0
      };
    }
  },

  getConsolidatedTWR: async (): Promise<TWRPerformance> => {
    const response = await api.get('/admin/performance/consolidated/twr');
    const data = response.data;

    // Transform the backend response to match the expected TWRPerformance interface
    let aggregatedTWR = {
      daily: 0,
      weekly: 0,
      monthly: 0,
      total: 0,
      last_calculated: new Date().toISOString()
    };

    // If we have wallet data, calculate weighted average TWR based on wallet values
    if (data.wallets && data.wallets.length > 0) {
      let totalValue = 0;
      let weightedDaily = 0;
      let weightedWeekly = 0;
      let weightedMonthly = 0;
      let weightedTotal = 0;

      data.wallets.forEach((wallet: any) => {
        const walletValue = wallet.current_value || 0;
        totalValue += walletValue;

        if (wallet.twr) {
          weightedDaily += (wallet.twr.daily || 0) * walletValue;
          weightedWeekly += (wallet.twr.weekly || 0) * walletValue;
          weightedMonthly += (wallet.twr.monthly || 0) * walletValue;
          weightedTotal += (wallet.twr.total || 0) * walletValue;
        }
      });

      if (totalValue > 0) {
        aggregatedTWR.daily = weightedDaily / totalValue;
        aggregatedTWR.weekly = weightedWeekly / totalValue;
        aggregatedTWR.monthly = weightedMonthly / totalValue;
        aggregatedTWR.total = weightedTotal / totalValue;
      }
    }

    // If portfolio has twr_percent but no nested twr object, use it for total
    if (data.portfolio && typeof data.portfolio.twr_percent === 'number' && !aggregatedTWR.total) {
      aggregatedTWR.total = data.portfolio.twr_percent;
    }

    const absoluteReturn = (data.portfolio?.current_value || 0) - (data.portfolio?.net_invested || 0);
    const absolutePercent = data.portfolio?.net_invested > 0
      ? (absoluteReturn / data.portfolio.net_invested * 100)
      : 0;

    return {
      portfolio_value: data.portfolio?.current_value || 0,
      absolute_return: absoluteReturn,
      absolute_percent: absolutePercent,
      twr: aggregatedTWR,
      net_invested: data.portfolio?.net_invested || 0,
      total_deposits: data.portfolio?.total_deposits || 0,
      total_withdrawals: data.portfolio?.total_withdrawals || 0,
      trading_pnl: absoluteReturn,
      roi_percent: absolutePercent,
      periods: {
        daily: {
          absolute_return: 0,  // Would need daily snapshot data to calculate
          absolute_percent: 0,
          twr_return: 0,
          twr_percent: aggregatedTWR.daily
        },
        weekly: {
          absolute_return: 0,  // Would need weekly snapshot data to calculate
          absolute_percent: 0,
          twr_return: 0,
          twr_percent: aggregatedTWR.weekly
        },
        monthly: {
          absolute_return: 0,  // Would need monthly snapshot data to calculate
          absolute_percent: 0,
          twr_return: 0,
          twr_percent: aggregatedTWR.monthly
        },
        total: {
          absolute_return: absoluteReturn,
          absolute_percent: absolutePercent,
          twr_return: 0,
          twr_percent: aggregatedTWR.total
        }
      }
    };
  },

  // Withdrawal API
  withdrawals: {
    getList: async (): Promise<WithdrawalRequest[]> => {
      const response = await api.get('/withdrawals/');
      // Handle pagination if needed
      return response.data.results || response.data;
    },

    create: async (request: WithdrawalCreateRequest): Promise<WithdrawalRequest> => {
      const response = await api.post('/withdrawals/create/', request);
      return response.data;
    },

    getDetail: async (id: number): Promise<WithdrawalRequest> => {
      const response = await api.get(`/withdrawals/${id}/`);
      return response.data;
    },

    cancel: async (id: number): Promise<WithdrawalRequest> => {
      const response = await api.post(`/withdrawals/${id}/cancel/`);
      return response.data;
    },

    getLimits: async (): Promise<WithdrawalLimits> => {
      const response = await api.get('/withdrawals/limits/');
      return response.data;
    },
  },

  // Swap API
  swap: {
    getList: async (): Promise<SwapRequest[]> => {
      const response = await api.get('/swap/');
      return response.data.results || response.data;
    },

    getBalances: async (): Promise<SwapBalances> => {
      const response = await api.get('/swap/balances/');
      return response.data;
    },

    getQuote: async (request: {
      input_token: 'SOL' | 'USDC';
      output_token: 'SOL' | 'USDC';
      input_amount: number;
      slippage_bps?: number;
    }): Promise<SwapQuote> => {
      const response = await api.post('/swap/quote/', request);
      return response.data;
    },

    create: async (request: SwapCreateRequest): Promise<SwapRequest> => {
      const response = await api.post('/swap/create/', request);
      return response.data;
    },

    getDetail: async (id: number): Promise<SwapRequest> => {
      const response = await api.get(`/swap/${id}/`);
      return response.data;
    },

    cancel: async (id: number): Promise<SwapRequest> => {
      const response = await api.post(`/swap/${id}/cancel/`);
      return response.data;
    },
  },

  // Wallet API (Django User Service)
  wallet: {
    getBalance: async () => {
      const response = await api.get('/wallets/balance/');
      // Django returns: { balance_lamports, balance_sol, has_deposited, min_required_sol }
      return response.data;
    },

    getAddress: async () => {
      const response = await api.get('/wallets/address/');
      // Django returns: { wallet_address }
      return response.data;
    },

    exportPrivateKey: async (password: string): Promise<{
      private_key: string;
      public_key: string;
      warning: string;
      format: string;
      exports_remaining: number;
    }> => {
      const response = await api.post('/wallets/export-private-key/', { password });
      // Django returns: { private_key, public_key, warning, format, exports_remaining }
      return response.data;
    },

    dismissSecurityReminder: async (): Promise<{
      message: string;
      dismissed_until: string;
    }> => {
      const response = await api.post('/wallets/dismiss-security-reminder/');
      return response.data;
    },
  },

  // Bot Management API (Django handles this now)
  bot: {
    start: async (password: string, skipInitialRebalance: boolean = false): Promise<{
      success: boolean;
      message: string;
      task_id?: string;
      status: string;
      error?: string;
    }> => {
      const response = await api.post('/bots/start/', {
        password,
        skip_initial_rebalance: skipInitialRebalance
      });
      return response.data;
    },

    stop: async (force: boolean = false): Promise<{
      success: boolean;
      message: string;
      task_id?: string;
      status: string;
      error?: string;
    }> => {
      const response = await api.post('/bots/stop/', { force });
      return response.data;
    },

    getStatus: async (): Promise<{
      status: 'starting' | 'running' | 'stopping' | 'stopped' | 'error' | 'not_configured';
      is_running: boolean;
      container_id?: string;
      api_port?: number;
      api_url?: string;
      started_at?: string;
      last_health_check?: string;
      health_status?: string;
      error_count?: number;
      last_error?: string;
      message?: string;
    }> => {
      const response = await api.get('/bots/status/');
      return response.data;
    },

    getHealth: async () => {
      const response = await api.get('/bots/health/');
      return response.data;
    },

    getDelta: async () => {
      const response = await api.get('/bots/delta/');
      return response.data;
    },

    shutdown: async (password: string): Promise<{
      success: boolean;
      message: string;
      orca_positions_closed: number;
      orca_positions_failed: number;
      drift_positions_closed: number;
      drift_positions_failed: number;
      drift_amount_withdrawn: number;
      errors: string[];
      error?: string;
    }> => {
      // Use extended timeout for shutdown operation (5 minutes)
      const response = await api.post('/bots/shutdown/', { password }, { timeout: 300000 });
      return response.data;
    },
  },

  // Subscription API
  subscription: {
    get: async (): Promise<SubscriptionStatus> => {
      const response = await api.get('/subscription/');
      return response.data;
    },
  },

  // Plan & Payment API
  plans: {
    getAll: async (): Promise<Plan[]> => {
      const response = await api.get('/plans/');
      return response.data;
    },
  },

  payments: {
    create: async (planId: number): Promise<PaymentRequest> => {
      const response = await api.post('/payments/create/', { plan_id: planId });
      return response.data;
    },

    getStatus: async (paymentId: number): Promise<PaymentStatusResponse> => {
      const response = await api.get(`/payments/${paymentId}/status/`);
      return response.data;
    },

    verify: async (paymentId: number): Promise<PaymentVerifyResponse> => {
      const response = await api.post(`/payments/${paymentId}/verify/`);
      return response.data;
    },

    getDetail: async (paymentId: number): Promise<PaymentRequest> => {
      const response = await api.get(`/payments/${paymentId}/`);
      return response.data;
    },

    getHistory: async (): Promise<PaymentRequest[]> => {
      const response = await api.get('/payments/');
      return response.data;
    },

    getLatestPending: async (): Promise<PaymentRequest | null> => {
      const response = await api.get('/payments/');
      const payments: PaymentRequest[] = response.data;
      // Find the most recent pending payment that hasn't expired
      const now = new Date();
      const pendingPayment = payments.find(p => {
        if (p.status !== 'pending') return false;
        const expiresAt = new Date(p.expires_at);
        return expiresAt > now;
      });
      return pendingPayment || null;
    },
  },

  // User Profile API
  profile: {
    get: async () => {
      const response = await api.get('/profile/');
      return response.data;
    },
  },

  // Auth extensions (Django User Service)
  changePassword: async (oldPassword: string, newPassword: string) => {
    const response = await api.post('/auth/change-password/', {
      old_password: oldPassword,
      new_password: newPassword,
      new_password_confirm: newPassword
    });
    return response.data;
  },

  // Onboarding API
  getOnboardingStatus: async (): Promise<OnboardingStatus> => {
    const response = await api.get('/auth/onboarding-status/');
    return response.data;
  },

  completeOnboarding: async (riskAcknowledged: boolean): Promise<CompleteOnboardingResponse> => {
    const response = await api.post('/auth/complete-onboarding/', {
      risk_acknowledged: riskAcknowledged,
    });
    return response.data;
  },

  // Vault / Gamification API
  vault: {
    getDashboard: async (): Promise<VaultDashboardResponse> => {
      const response = await api.get('/vault/dashboard/');
      return response.data;
    },

    getChart: async (period: VaultTimeframe = '1M'): Promise<VaultChartResponse> => {
      const response = await api.get('/vault/chart/', {
        params: { period }
      });
      return response.data;
    },

    getStreakHistory: async (limit: number = 30): Promise<VaultStreakHistoryResponse> => {
      const response = await api.get('/vault/streaks/history/', {
        params: { limit }
      });
      return response.data;
    },

    getMilestones: async (): Promise<VaultMilestonesResponse> => {
      const response = await api.get('/vault/milestones/');
      return response.data;
    },

    getNotifications: async (unreadOnly: boolean = false): Promise<VaultNotificationsResponse> => {
      const response = await api.get('/vault/notifications/', {
        params: { unread_only: unreadOnly }
      });
      return response.data;
    },

    markNotificationRead: async (notificationId: number): Promise<void> => {
      await api.post(`/vault/notifications/${notificationId}/read/`);
    },

    markAllNotificationsRead: async (): Promise<{ marked_count: number }> => {
      const response = await api.post('/vault/notifications/mark-all-read/');
      return response.data;
    },

    getExposure: async (): Promise<ExposureData> => {
      const response = await api.get('/exposure/');
      return response.data;
    },
  },

  // Activity Feed API
  activities: {
    getList: async (params?: {
      type?: string;
      start_date?: string;
      end_date?: string;
      limit?: number;
      offset?: number;
    }): Promise<ActivityListResponse> => {
      const response = await api.get('/activities/', { params });
      return response.data;
    },

    getRecent: async (limit: number = 5): Promise<RecentActivitiesResponse> => {
      const response = await api.get('/activities/recent/', {
        params: { limit }
      });
      return response.data;
    },
  },

  // Referral API
  referral: {
    getCode: async (): Promise<ReferralCode> => {
      const response = await api.get('/referral/code/');
      return response.data;
    },

    getStats: async (): Promise<ReferralStats> => {
      const response = await api.get('/referral/stats/');
      return response.data;
    },

    regenerateCode: async (): Promise<ReferralCode> => {
      const response = await api.post('/referral/regenerate/');
      return response.data;
    },
  },

  // Telegram API
  telegram: {
    getLinkCode: async (): Promise<TelegramLinkCode> => {
      const response = await api.get('/telegram/link-code/');
      return response.data;
    },

    getStatus: async (): Promise<TelegramStatus> => {
      const response = await api.get('/telegram/status/');
      return response.data;
    },

    updatePreferences: async (preferences: TelegramPreferencesUpdateRequest): Promise<TelegramPreferencesUpdateResponse> => {
      const response = await api.patch('/telegram/preferences/', preferences);
      return response.data;
    },

    unlink: async (): Promise<TelegramUnlinkResponse> => {
      const response = await api.post('/telegram/unlink/');
      return response.data;
    },
  },
};

// Vault API Types
export type VaultTimeframe = '1D' | '1W' | '1M' | '3M' | '1Y' | 'ALL';

export interface VaultChartDataPoint {
  timestamp: string;
  value: number;
  sol_price?: number;
}

export interface VaultStreakData {
  current: {
    type: 'winning' | 'losing' | 'none';
    count: number;
    start_date: string | null;
  };
  best_winning: {
    count: number;
    start_date: string | null;
    end_date: string | null;
  };
  best_losing: {
    count: number;
    start_date: string | null;
    end_date: string | null;
  };
  stats: {
    winning_days: number;
    losing_days: number;
    flat_days: number;
    win_rate: number;
  };
}

export interface VaultATHData {
  value: number;
  date: string | null;
  is_at_ath: boolean;
  times_broken: number;
}

export interface VaultMilestoneData {
  id: number;
  name: string;
  description: string;
  value: number;
  achieved_at?: string;
  achieved_value?: number;
  icon: string;
  badge_color: string;
}

export interface VaultNextMilestone {
  id: number;
  name: string;
  value: number;
  icon: string;
  progress_percent: number;
}

export interface VaultDashboardResponse {
  portfolio: {
    current_value: number;
    change_24h: number;
    change_24h_percent: number;
    sol_price: number;
    timestamp: string | null;
  };
  streaks: VaultStreakData;
  ath: VaultATHData;
  milestones: {
    achieved: VaultMilestoneData[];
    next: VaultNextMilestone | null;
    total_achieved: number;
  };
  notifications: {
    unread_count: number;
  };
  last_updated: string;
  is_new_user: boolean;
}

export interface VaultChartResponse {
  data: VaultChartDataPoint[];
  period: VaultTimeframe;
  change: number;
  change_percent: number;
}

export interface VaultDailyPerformanceRecord {
  date: string;
  result: 'winning' | 'losing' | 'flat';
  change_usd: number;
  change_percent: number;
  closing_equity: number;
  is_ath: boolean;
}

export interface VaultStreakHistoryResponse {
  history: VaultDailyPerformanceRecord[];
  total_days: number;
}

export interface VaultMilestonesResponse {
  achieved: VaultMilestoneData[];
  available: VaultMilestoneData[];
  next: VaultNextMilestone | null;
  total_achieved: number;
  total_available: number;
}

export interface VaultNotification {
  id: number;
  type: 'milestone' | 'ath' | 'streak';
  title: string;
  message: string;
  related_value: number | null;
  related_date: string | null;
  is_read: boolean;
  created_at: string;
}

export interface VaultNotificationsResponse {
  notifications: VaultNotification[];
  unread_count: number;
  total_count: number;
}

// Exposure/Risk Dashboard Types
export type PositionHealth = 'healthy' | 'warning' | 'critical' | 'inactive' | 'unknown';
export type VolatilityRegime = 'low' | 'normal' | 'high' | 'extreme' | 'unknown';
export type ExposureStatus = 'active' | 'no_bot' | 'bot_stopped' | 'unavailable';

export interface ExposurePosition {
  pool_id: string;
  token_a_balance: number;
  token_b_balance: number;
  delta_usd: number;
  in_range: boolean;
}

export interface ExposureData {
  status: ExposureStatus;
  message: string;
  lp_delta: number;
  lp_delta_usd?: number;
  lp_notional?: number;
  hedge_delta: number;
  hedge_delta_usd?: number;
  net_delta: number;
  net_delta_usd?: number;
  net_delta_percent?: number;
  position_health: PositionHealth;
  volatility_regime: VolatilityRegime;
  positions: ExposurePosition[];
  positions_count?: number;
  sol_price?: number;
  last_updated: string;
}

// Activity Types
export type ActivityType =
  | 'deposit'
  | 'withdrawal'
  | 'auto_deposit'
  | 'bot_started'
  | 'bot_stopped'
  | 'position_opened'
  | 'position_closed'
  | 'rebalance'
  | 'fee_harvest'
  | 'hedge_opened'
  | 'hedge_adjusted'
  | 'regime_change';

export interface Activity {
  id: number;
  type: ActivityType;
  title: string;
  description: string;
  details: Record<string, unknown>;
  amount: number | null;
  tx_signature: string | null;
  created_at: string;
  icon: string;
  color: string;
}

export interface ActivityListResponse {
  activities: Activity[];
  total: number;
  has_more: boolean;
  limit: number;
  offset: number;
}

export interface RecentActivitiesResponse {
  activities: Activity[];
  count: number;
}

// Telegram Types
export interface TelegramLinkCode {
  linking_code: string;
  expires_in_seconds: number;
  instructions: string;
}

export interface TelegramNotificationPreferences {
  notify_bot_status: boolean;
  notify_errors: boolean;
  notify_milestones: boolean;
  notify_weekly_summary: boolean;
  notify_transactions: boolean;
}

export interface TelegramStatus {
  is_linked: boolean;
  preferences: TelegramNotificationPreferences | null;
}

export interface TelegramPreferencesUpdateRequest {
  notify_bot_status?: boolean;
  notify_errors?: boolean;
  notify_milestones?: boolean;
  notify_weekly_summary?: boolean;
  notify_transactions?: boolean;
}

export interface TelegramPreferencesUpdateResponse {
  message: string;
  preferences: TelegramNotificationPreferences;
}

export interface TelegramUnlinkResponse {
  message: string;
}

// Referral Types
export interface ReferralCode {
  code: string;
  is_active: boolean;
  created_at: string;
  referral_link: string;
}

export interface Referral {
  id: number;
  referee_email: string;
  status: 'pending' | 'active' | 'churned';
  status_display: string;
  created_at: string;
  converted_at: string | null;
  credited_at: string | null;
}

export interface ReferralStats {
  total_invited: number;
  active_subscribers: number;
  pending: number;
  months_earned: number;
  referrals: Referral[];
}

export default apiService;